import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import scannerApi from './scannerApi';

const READER_ID = 'accred-scan-reader';
const COOLDOWN_MS = 2500; // ignore the same code while the camera still sees it
const PLACE_KEY = 'scanner_place';
const TYPE_LABEL = { venue: 'Venue gate', zone: 'Zone', dining: 'Dining', transport: 'Transport' };

export const STATUS_TEXT = {
  active: 'ACCESS ALLOWED',
  blocked: 'BLOCKED',
  revoked: 'REVOKED',
  not_approved: 'NOT APPROVED',
  not_found: 'NOT FOUND',
  no_permission: 'NO PERMISSION HERE',
  outside_area: 'OUTSIDE THE AREA',
};

const keyOf = (pl) => `${pl.type}:${pl.id}`;
const AUTO = 'auto';
const FRESH_MS = 2 * 60 * 1000; // a location older than this is not trusted

const distanceM = (a, b) => {
  const rad = (d) => (d * Math.PI) / 180;
  const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
};

// nearest of the guard's own places whose area contains the phone (GPS accuracy forgiven, as on the server);
// no match -> the venue gate (first place), which is where the server will flag or refuse a scan made elsewhere
const nearestPlace = (places, pos) => {
  if (!pos || Date.now() - pos.at > FRESH_MS) return places[0];
  let best = null;
  for (const p of places) {
    if (!p.geo) continue;
    const d = distanceM(pos, p.geo);
    if (d <= p.geo.radius + Math.min(pos.accuracy || 0, 100) && (!best || d < best.d)) best = { p, d };
  }
  return best ? best.p : places[0];
};

// Reads the card QR with the camera and asks the API for the card's *current* status, so a block/revoke
// made after printing or handover is honoured on the next scan. Each scan is recorded against this login.
// With a place selected the scan is also an entry or exit at that place: the first scan is IN, the next OUT, and so on.
const ScanPage = () => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const [places, setPlaces] = useState([]);
  const [placeKey, setPlaceKey] = useState(''); // '' = only check the card, don't count an entry
  const [undoMsg, setUndoMsg] = useState('');
  const [pos, setPos] = useState(null); // { lat, lng, accuracy, at } from the phone's GPS
  const [geoState, setGeoState] = useState('waiting'); // waiting | ok | denied | unavailable
  const last = useRef({ text: '', at: 0 });
  const placesRef = useRef([]);
  const placeKeyRef = useRef('');
  const posRef = useRef(null);

  placesRef.current = places;
  placeKeyRef.current = placeKey;
  posRef.current = pos;

  useEffect(() => {
    scannerApi
      .get('/security/me')
      .then(({ data }) => {
        const list = data.data.places;
        setPlaces(list);
        let saved = null;
        try { saved = localStorage.getItem(PLACE_KEY); } catch { /* storage blocked */ }
        // '' = check card only; a saved place that is no longer assigned falls back to automatic
        setPlaceKey(saved === '' || saved === AUTO || list.some((p) => keyOf(p) === saved) ? saved : AUTO);
      })
      .catch(() => {}); // without a profile the scanner still checks cards, it just can't count entries
  }, []);

  // the phone's position, kept fresh while the scanner is open; sent with every scan so the server can check it
  useEffect(() => {
    if (!('geolocation' in navigator)) { setGeoState('unavailable'); return undefined; }
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy), at: Date.now() });
        setGeoState('ok');
      },
      (e) => setGeoState(e.code === 1 ? 'denied' : 'unavailable'),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // the place a scan counts at: the chosen one, or (automatic) the nearest of the guard's places
  const activePlace = () => {
    const list = placesRef.current;
    const key = placeKeyRef.current;
    if (!list.length || key === '') return null;
    return key === AUTO ? nearestPlace(list, posRef.current) : list.find((p) => keyOf(p) === key) || null;
  };

  const choosePlace = (key) => {
    setPlaceKey(key);
    setResult(null);
    try { localStorage.setItem(PLACE_KEY, key); } catch { /* storage blocked */ }
  };

  const check = async (body) => {
    const place = activePlace();
    const here = posRef.current && Date.now() - posRef.current.at < FRESH_MS ? posRef.current : null;
    setUndoMsg('');
    try {
      const { data } = await scannerApi.post(
        '/handover/scan',
        place ? { ...body, checkpoint: { type: place.type, id: place.id }, ...(here && { location: { lat: here.lat, lng: here.lng, accuracy: here.accuracy } }) } : body
      );
      setResult(data);
    } catch (e) {
      // a verdict body on 4xx/5xx is shown as-is; a network failure must never read as "allowed"
      setResult(
        e.response?.data?.status
          ? e.response.data
          : { allowed: false, status: 'error', reason: e.response?.data?.message || 'Could not reach the server. Check your connection.' }
      );
    }
  };

  const checkRef = useRef(check); // the camera callback below lives for the whole visit; it must call the latest check
  checkRef.current = check;

  const undo = async () => {
    try {
      await scannerApi.post('/handover/scan/undo', { log_id: result.log_id });
      setResult(null);
      last.current = { text: '', at: 0 };
      setUndoMsg('Last scan undone. It will not be counted.');
    } catch (e) {
      setUndoMsg(e.response?.data?.message || 'Could not undo that scan.');
    }
  };

  useEffect(() => {
    const onDecode = (text) => {
      const now = Date.now();
      if (text === last.current.text && now - last.current.at < COOLDOWN_MS) return;
      last.current = { text, at: now };
      checkRef.current({ qr: text });
    };

    const s = new Html5Qrcode(READER_ID);
    s.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 250, height: 250 } }, onDecode, () => {}).catch((e) =>
      setError(`Camera unavailable: ${e?.message || e}. Allow camera access (HTTPS required) or enter the ID below.`)
    );
    return () => {
      if (s.isScanning) s.stop().then(() => s.clear()).catch(() => {});
      else s.clear();
    };
  }, []);

  const isIn = result?.direction === 'in';
  const color = !result ? '#6c757d' : !result.allowed ? '#dc3545' : result.direction ? (isIn ? '#198754' : '#0d6efd') : '#198754';
  const banner = !result
    ? ''
    : result.allowed && result.direction
      ? `${isIn ? 'IN' : 'OUT'}${result.duplicate ? ' (same scan)' : ''}`
      : STATUS_TEXT[result.status] || 'ERROR';

  return (
    <>
      {places.length > 0 && (
        <div className="mb-3">
          <label className="form-label small text-muted mb-1">Scanning at</label>
          <select className="form-select" value={placeKey} onChange={(e) => choosePlace(e.target.value)}>
            <option value={AUTO}>Automatic: nearest place (uses GPS)</option>
            {places.map((p) => (
              <option key={keyOf(p)} value={keyOf(p)}>{`${TYPE_LABEL[p.type]}: ${p.name}`}</option>
            ))}
            <option value="">Check card only (don't count entry/exit)</option>
          </select>
          <div className="small mt-1">
            {placeKey === AUTO && activePlace() && (
              <span className="me-2">
                Using: <strong>{activePlace().name}</strong>
              </span>
            )}
            {geoState === 'ok' && <span className="text-success">Location on (±{pos?.accuracy ?? '?'} m)</span>}
            {geoState === 'waiting' && <span className="text-muted">Getting your location…</span>}
            {(geoState === 'denied' || geoState === 'unavailable') && (
              <span className="text-danger">
                {geoState === 'denied' ? 'Location is blocked' : 'Location unavailable'}: scans will be marked "location unknown".
              </span>
            )}
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {undoMsg && <div className="alert alert-info py-2">{undoMsg}</div>}
      <div id={READER_ID} style={{ width: '100%', background: '#000', borderRadius: 8, overflow: 'hidden' }} />

      {result && (
        <div className="card mt-3" style={{ border: `3px solid ${color}` }}>
          <div className="text-white text-center fw-bold py-2 fs-4" style={{ background: color }}>
            {banner}
          </div>
          <div className="card-body">
            {result.checkpoint && (
              <div className="mb-2 small text-muted">
                {TYPE_LABEL[result.checkpoint.type]}: <strong>{result.checkpoint.name}</strong>
                {result.allowed && result.direction && <> · entry #{result.entries_today} today</>}
              </div>
            )}
            {result.user && (
              <div className="d-flex gap-3 align-items-center mb-2">
                {result.user.photo && <img src={result.user.photo} alt="" width={80} height={80} style={{ objectFit: 'cover', borderRadius: 8 }} />}
                <div>
                  <div className="fw-bold fs-5">{result.user.name}</div>
                  <div>{result.user.user_type}</div>
                  <div className="text-muted small">{result.user.organisation}</div>
                </div>
              </div>
            )}
            {result.reason && (
              <div className="mb-2">
                <strong>Reason:</strong> {result.reason}
              </div>
            )}
            {result.geo?.status === 'outside' && result.status !== 'outside_area' && (
              <div className="alert alert-warning py-2 mb-2">
                Location check: you are about {result.geo.distance} m from {result.checkpoint?.name} (area {result.geo.radius} m). This scan is flagged.
              </div>
            )}
            {result.allowed && (
              <>
                <div>
                  <strong>Zones:</strong> {result.zones?.length ? result.zones.join(', ') : '—'}
                </div>
                <div>
                  <strong>Dining:</strong> {result.dining?.length ? result.dining.join(', ') : '—'}
                </div>
              </>
            )}
            <div className="d-flex gap-2 mt-3">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  last.current = { text: '', at: 0 };
                  setResult(null);
                }}
              >
                Clear
              </button>
              {result.allowed && result.direction && result.log_id && !result.duplicate && (
                <button className="btn btn-outline-danger btn-sm" onClick={undo}>
                  Undo this scan
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <form
        className="input-group mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (manual.trim()) check({ qr: manual.trim() });
        }}
      >
        <input className="form-control" placeholder="Enter accreditation ID manually" value={manual} onChange={(e) => setManual(e.target.value)} />
        <button className="btn btn-primary" type="submit">
          Check
        </button>
      </form>
    </>
  );
};

export default ScanPage;
